import { useEffect, useState } from "react";
import { Trash2, Edit, Plus, TrendingUp, ShoppingBag, Users, Mail } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";
import { formatPrice } from "../lib/format";
import ImageUploader, { ImageThumb } from "../components/ImageUploader";
import AdminReviewsTab from "../components/AdminReviewsTab";
import AdminCJTab from "../components/AdminCJTab";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const empty = {
  name: "",
  description: "",
  price: 0,
  image: "",
  images: [],
  category: "smart",
  stock: 100,
  featured: false,
  best_seller: false,
  variant_attributes: [],
  variants: [],
  cj_vid: "",
  cj_pid: "",
};

export default function Admin() {
  const [tab, setTab] = useState("dashboard");
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [subs, setSubs] = useState([]);
  const [stats, setStats] = useState(null);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const refresh = async () => {
    const [pr, or, ct, ns, st, rc] = await Promise.all([
      api.get("/products"),
      api.get("/orders"),
      api.get("/contacts"),
      api.get("/newsletter"),
      api.get("/admin/stats").catch(() => ({ data: null })),
      api.get("/admin/coupons/recovery").catch(() => ({ data: null })),
    ]);
    setProducts(pr.data);
    setOrders(or.data);
    setContacts(ct.data);
    setSubs(ns.data);
    setStats(st.data ? { ...st.data, recovery: rc.data } : null);
  };

  useEffect(() => {
    refresh();
  }, []);

  const openNew = () => {
    setForm(empty);
    setEditing({});
  };
  const openEdit = (p) => {
    setForm({ ...p, images: Array.isArray(p.images) ? p.images : [] });
    setEditing(p);
  };
  const close = () => setEditing(null);

  const save = async () => {
    try {
      const extras = Array.isArray(form.images)
        ? form.images
        : String(form.images || "")
            .split(/\r?\n/)
            .map((s) => s.trim())
            .filter(Boolean);
      const payload = {
        name: form.name,
        description: form.description,
        price: parseFloat(form.price),
        image: form.image,
        images: extras,
        category: form.category,
        stock: parseInt(form.stock),
        featured: !!form.featured,
        best_seller: !!form.best_seller,
        variant_attributes: Array.isArray(form.variant_attributes)
          ? form.variant_attributes
          : [],
        variants: Array.isArray(form.variants) ? form.variants : [],
        cj_vid: form.cj_vid || null,
        cj_pid: form.cj_pid || null,
      };
      if (editing && editing.id) {
        await api.put(`/products/${editing.id}`, payload);
        toast.success("Produit mis à jour");
      } else {
        await api.post("/products", payload);
        toast.success("Produit créé");
      }
      close();
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const del = async (id) => {
    if (!confirm("Supprimer ce produit ?")) return;
    await api.delete(`/products/${id}`);
    toast.success("Supprimé");
    refresh();
  };

  const updateStatus = async (orderId, status) => {
    await api.put(`/orders/${orderId}/status`, null, { params: { status } });
    toast.success("Statut mis à jour");
    refresh();
  };

  const tabs = [
    { id: "dashboard", label: "Dashboard" },
    { id: "products", label: "Produits" },
    { id: "orders", label: "Commandes" },
    { id: "reviews", label: "Avis" },
    { id: "contacts", label: "Messages" },
    { id: "newsletter", label: "Newsletter" },
    { id: "cj", label: "CJ Dropshipping" },
  ];

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-12 py-12" data-testid="admin-page">
      <div className="border-b border-white/10 pb-8 mb-12">
        <div className="text-xs font-bold uppercase tracking-[0.3em] text-[#C5CDD8]">Administration</div>
        <h1 className="font-display mt-3 text-4xl sm:text-5xl font-bold uppercase tracking-tighter text-white">
          Tableau de bord
        </h1>
      </div>

      <div className="flex gap-2 mb-8 border-b border-white/10 overflow-x-auto">
        {tabs.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`px-6 py-3 text-xs font-bold uppercase tracking-[0.25em] border-b-2 -mb-px whitespace-nowrap ${
              tab === t.id ? "border-white text-white" : "border-transparent text-white/50 hover:text-white"
            }`}
            data-testid={`admin-tab-${t.id}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "dashboard" && (
        <div data-testid="admin-dashboard-tab" className="space-y-10">
          {!stats ? (
            <div className="p-12 text-center text-sm text-white/60 border border-white/10 bg-[#0A2A4F]">
              Chargement des statistiques...
            </div>
          ) : (
            <>
              {/* KPI Cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Kpi
                  icon={TrendingUp}
                  label="Revenu total"
                  value={formatPrice(stats.revenue_total)}
                  hint={`30j : ${formatPrice(stats.revenue_30d)}`}
                  testid="kpi-revenue"
                />
                <Kpi
                  icon={ShoppingBag}
                  label="Commandes payées"
                  value={stats.orders_paid}
                  hint={`Total : ${stats.orders_total}`}
                  testid="kpi-orders"
                />
                <Kpi
                  icon={TrendingUp}
                  label="Panier moyen"
                  value={formatPrice(stats.aov)}
                  hint={`Conv. ${stats.conversion_rate}%`}
                  testid="kpi-aov"
                />
                <Kpi
                  icon={Users}
                  label="Utilisateurs"
                  value={stats.users_count}
                  hint={`Newsletter : ${stats.newsletter_count}`}
                  testid="kpi-users"
                />
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Kpi
                  label="7 derniers jours"
                  value={formatPrice(stats.revenue_7d)}
                  hint={`${stats.orders_7d} commandes`}
                  testid="kpi-7d"
                />
                <Kpi
                  label="Paniers abandonnés"
                  value={stats.orders_abandoned}
                  hint="À récupérer"
                  testid="kpi-abandoned"
                />
                <Kpi
                  icon={Mail}
                  label="Messages non lus"
                  value={stats.unread_contacts}
                  hint="Contact"
                  testid="kpi-unread"
                />
                <Kpi
                  label="Commandes 30j"
                  value={stats.orders_30d}
                  hint={`AOV : ${formatPrice(stats.aov)}`}
                  testid="kpi-orders30"
                />
              </div>

              {/* Recovery coupons performance */}
              {stats.recovery && (
                <div className="mt-10">
                  <div className="text-xs font-bold uppercase tracking-[0.25em] text-[#C5CDD8] mb-4">
                    Récupération paniers abandonnés (–15% personnalisés)
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <Kpi
                      label="Coupons émis"
                      value={stats.recovery.issued}
                      hint="Vague J+3"
                      testid="kpi-recovery-issued"
                    />
                    <Kpi
                      label="Coupons utilisés"
                      value={stats.recovery.redeemed}
                      hint={`Taux : ${stats.recovery.redemption_rate_pct}%`}
                      testid="kpi-recovery-redeemed"
                    />
                    <Kpi
                      label="Commandes récupérées"
                      value={stats.recovery.redeemed_orders}
                      hint="Paniers récupérés"
                      testid="kpi-recovery-orders"
                    />
                    <Kpi
                      label="CA récupéré"
                      value={formatPrice(stats.recovery.recovered_revenue)}
                      hint="Grâce aux emails"
                      testid="kpi-recovery-revenue"
                    />
                  </div>
                </div>
              )}

              {/* Top products */}
              <div>
                <div className="text-xs font-bold uppercase tracking-[0.25em] text-[#C5CDD8] mb-4">
                  Top produits (par quantité vendue)
                </div>
                <div className="border border-white/10 bg-[#0A2A4F] divide-y divide-white/10">
                  {stats.top_products.length === 0 && (
                    <div className="p-8 text-center text-sm text-white/60">
                      Aucune vente pour le moment.
                    </div>
                  )}
                  {stats.top_products.map((t, i) => (
                    <div
                      key={t.product_id}
                      className="flex items-center gap-4 p-4"
                      data-testid={`top-product-${i}`}
                    >
                      <div className="font-display text-2xl font-bold text-[#C5CDD8] w-8">
                        {i + 1}
                      </div>
                      <div className="w-14 h-14 bg-[#001F3F] flex-shrink-0 border border-white/5">
                        <img
                          src={t.image}
                          alt={t.name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-white truncate">{t.name}</div>
                        <div className="text-xs text-white/60 uppercase tracking-widest mt-1">
                          {t.qty} vendus · {formatPrice(t.revenue)} de revenu
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Daily chart (simple bar) */}
              {stats.daily.length > 0 && (
                <div>
                  <div className="text-xs font-bold uppercase tracking-[0.25em] text-[#C5CDD8] mb-4">
                    Revenu sur 14 jours
                  </div>
                  <div className="border border-white/10 bg-[#0A2A4F] p-6">
                    <div className="flex items-end gap-2 h-48">
                      {stats.daily.map((d) => {
                        const max = Math.max(
                          ...stats.daily.map((x) => x.revenue),
                          1
                        );
                        const h = (d.revenue / max) * 100;
                        return (
                          <div
                            key={d.date}
                            className="flex-1 flex flex-col items-center gap-1"
                            title={`${d.date}: ${formatPrice(d.revenue)}`}
                          >
                            <div
                              className="w-full bg-white/80 hover:bg-white transition"
                              style={{ height: `${h}%`, minHeight: "2px" }}
                            />
                            <div className="text-[9px] text-white/50 uppercase tracking-widest">
                              {d.date.slice(5)}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {tab === "products" && (
        <div data-testid="admin-products-tab">
          <div className="flex justify-between items-center mb-6">
            <p className="text-sm text-white/60">{products.length} produits</p>
            <button
              onClick={openNew}
              className="inline-flex items-center gap-2 border border-white bg-white text-[#001F3F] hover:bg-transparent hover:text-white transition-all uppercase tracking-[0.25em] text-xs font-bold px-6 py-3"
              data-testid="admin-new-product"
            >
              <Plus size={14} /> Nouveau
            </button>
          </div>
          <div className="border border-white/10 bg-[#0A2A4F] divide-y divide-white/10">
            {products.map((p) => (
              <div key={p.id} className="flex items-center gap-4 p-4">
                <div className="w-14 h-14 bg-[#001F3F] flex-shrink-0 border border-white/5">
                  <img src={p.image} alt={p.name} className="w-full h-full object-cover" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-white truncate">{p.name}</div>
                  <div className="text-xs text-white/60 uppercase tracking-widest">
                    {p.category} · {formatPrice(p.price)} · stock {p.stock}
                  </div>
                </div>
                <button onClick={() => openEdit(p)} className="p-2 text-white/70 hover:bg-white hover:text-[#001F3F]" data-testid={`edit-product-${p.id}`}>
                  <Edit size={14} />
                </button>
                <button onClick={() => del(p.id)} className="p-2 text-white/70 hover:bg-[#FF6B6B] hover:text-white" data-testid={`delete-product-${p.id}`}>
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "orders" && (
        <div data-testid="admin-orders-tab">
          <p className="text-sm text-white/60 mb-6">{orders.length} commandes</p>
          <div className="border border-white/10 bg-[#0A2A4F] divide-y divide-white/10">
            {orders.map((o) => (
              <div key={o.id} className="p-4 grid sm:grid-cols-12 gap-4 items-center">
                <div className="sm:col-span-3">
                  <div className="text-xs font-bold uppercase tracking-widest text-white">#{o.id.slice(0, 8)}</div>
                  <div className="text-xs text-white/60 mt-1">{o.user_email || "Invité"}</div>
                </div>
                <div className="sm:col-span-3 text-xs text-white/60">
                  {new Date(o.created_at).toLocaleDateString("fr-FR")}
                </div>
                <div className="sm:col-span-2 font-display text-lg font-bold text-white">
                  {formatPrice(o.total)}
                </div>
                <div className="sm:col-span-2 text-xs uppercase tracking-widest text-white/60">
                  {o.payment_status}
                </div>
                <div className="sm:col-span-2">
                  <Select value={o.status} onValueChange={(v) => updateStatus(o.id, v)}>
                    <SelectTrigger className="rounded-none border-white/30 bg-[#001F3F] text-white h-9 text-xs" data-testid={`order-status-${o.id}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="rounded-none bg-[#0A2A4F] text-white border-white/20">
                      {["pending", "paid", "shipped", "delivered", "cancelled"].map((s) => (
                        <SelectItem key={s} value={s} className="rounded-none text-xs focus:bg-white/10">
                          {s}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            ))}
            {orders.length === 0 && (
              <div className="p-8 text-center text-sm text-white/60">Aucune commande.</div>
            )}
          </div>
        </div>
      )}

      {tab === "reviews" && <AdminReviewsTab />}
      {tab === "cj" && <AdminCJTab />}

      {tab === "contacts" && (
        <div data-testid="admin-contacts-tab">
          <p className="text-sm text-white/60 mb-6">{contacts.length} messages reçus</p>
          <div className="space-y-3">
            {contacts.map((c) => (
              <div key={c.id} className="border border-white/10 bg-[#0A2A4F] p-5">
                <div className="flex flex-wrap justify-between gap-2 mb-2">
                  <div>
                    <div className="text-sm font-bold text-white">{c.name}</div>
                    <div className="text-xs text-[#C5CDD8]">{c.email}</div>
                  </div>
                  <div className="text-xs text-white/60">
                    {new Date(c.created_at).toLocaleDateString("fr-FR")}
                  </div>
                </div>
                <div className="text-xs font-bold uppercase tracking-widest text-white/80 mb-2">
                  {c.subject}
                </div>
                <p className="text-sm text-white/70 whitespace-pre-line">{c.message}</p>
              </div>
            ))}
            {contacts.length === 0 && (
              <div className="border border-white/10 bg-[#0A2A4F] p-8 text-center text-sm text-white/60">
                Aucun message pour le moment.
              </div>
            )}
          </div>
        </div>
      )}

      {tab === "newsletter" && (
        <div data-testid="admin-newsletter-tab">
          <p className="text-sm text-white/60 mb-6">{subs.length} inscrits à la newsletter</p>
          <div className="border border-white/10 bg-[#0A2A4F] divide-y divide-white/10">
            {subs.map((s) => (
              <div key={s.id} className="p-4 flex justify-between items-center">
                <span className="text-sm text-white">{s.email}</span>
                <span className="text-xs text-white/60">
                  {new Date(s.created_at).toLocaleDateString("fr-FR")}
                </span>
              </div>
            ))}
            {subs.length === 0 && (
              <div className="p-8 text-center text-sm text-white/60">
                Aucune inscription pour le moment.
              </div>
            )}
          </div>
        </div>
      )}

      <Dialog open={!!editing} onOpenChange={(o) => !o && close()}>
        <DialogContent className="rounded-none max-w-lg bg-[#0A2A4F] text-white border-white/20">
          <DialogHeader>
            <DialogTitle className="font-display uppercase tracking-tighter text-white">
              {editing?.id ? "Modifier produit" : "Nouveau produit"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <input
              placeholder="Nom"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="w-full border-b border-white/40 bg-transparent text-white px-0 py-2 text-sm focus:outline-none"
              data-testid="form-product-name"
            />
            <textarea
              placeholder="Description"
              rows={3}
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              className="w-full border border-white/20 bg-[#001F3F] text-white px-3 py-2 text-sm focus:outline-none focus:border-white"
              data-testid="form-product-desc"
            />
            <div className="grid grid-cols-2 gap-4">
              <input
                type="number"
                step="0.01"
                placeholder="Prix"
                value={form.price}
                onChange={(e) => setForm({ ...form, price: e.target.value })}
                className="w-full border-b border-white/40 bg-transparent text-white px-0 py-2 text-sm focus:outline-none"
                data-testid="form-product-price"
              />
              <input
                type="number"
                placeholder="Stock"
                value={form.stock}
                onChange={(e) => setForm({ ...form, stock: e.target.value })}
                className="w-full border-b border-white/40 bg-transparent text-white px-0 py-2 text-sm focus:outline-none"
                data-testid="form-product-stock"
              />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-white/60 mb-2 font-bold">
                Image principale
              </div>
              <div className="flex items-start gap-3">
                {form.image && (
                  <ImageThumb
                    url={form.image}
                    onRemove={() => setForm({ ...form, image: "" })}
                  />
                )}
                <div className="flex-1 space-y-2">
                  <ImageUploader
                    label={form.image ? "Remplacer l'image" : "Glissez votre image principale"}
                    onUploaded={(url) => setForm({ ...form, image: url })}
                    testId="upload-main-image"
                  />
                  <input
                    placeholder="…ou collez une URL externe"
                    value={form.image}
                    onChange={(e) => setForm({ ...form, image: e.target.value })}
                    className="w-full border-b border-white/40 bg-transparent text-white px-0 py-2 text-xs focus:outline-none"
                    data-testid="form-product-image"
                  />
                </div>
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-white/60 mb-2 font-bold">
                Images supplémentaires ({Array.isArray(form.images) ? form.images.length : 0})
              </div>
              {Array.isArray(form.images) && form.images.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-3">
                  {form.images.map((u, i) => (
                    <ImageThumb
                      key={`${u}-${i}`}
                      url={u}
                      onRemove={() =>
                        setForm({
                          ...form,
                          images: form.images.filter((_, idx) => idx !== i),
                        })
                      }
                    />
                  ))}
                </div>
              )}
              <ImageUploader
                label="Ajouter une image secondaire"
                onUploaded={(url) =>
                  setForm({
                    ...form,
                    images: [...(Array.isArray(form.images) ? form.images : []), url],
                  })
                }
                testId="upload-extra-image"
              />
              <textarea
                placeholder="…ou collez plusieurs URLs (une par ligne)"
                rows={2}
                value={
                  Array.isArray(form.images) ? form.images.join("\n") : form.images
                }
                onChange={(e) =>
                  setForm({
                    ...form,
                    images: e.target.value
                      .split(/\r?\n/)
                      .map((s) => s.trim())
                      .filter(Boolean),
                  })
                }
                className="w-full mt-2 border border-white/20 bg-[#001F3F] text-white px-3 py-2 text-xs focus:outline-none focus:border-white"
                data-testid="form-product-images"
              />
            </div>
            <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
              <SelectTrigger className="rounded-none border-white/40 bg-[#001F3F] text-white" data-testid="form-product-category">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="rounded-none bg-[#0A2A4F] text-white border-white/20">
                <SelectItem value="smart" className="rounded-none focus:bg-white/10">Smart Tech</SelectItem>
                <SelectItem value="wellness" className="rounded-none focus:bg-white/10">Wellness</SelectItem>
              </SelectContent>
            </Select>
            <label className="flex items-center gap-2 text-xs uppercase tracking-widest font-bold text-white">
              <input
                type="checkbox"
                checked={form.featured}
                onChange={(e) => setForm({ ...form, featured: e.target.checked })}
                data-testid="form-product-featured"
              />
              En vedette
            </label>
            <label className="flex items-center gap-2 text-xs uppercase tracking-widest font-bold text-white">
              <input
                type="checkbox"
                checked={form.best_seller}
                onChange={(e) => setForm({ ...form, best_seller: e.target.checked })}
                data-testid="form-product-bestseller"
              />
              Best-seller (badge ★)
            </label>
          </div>

          {/* ─── Variants section ─── */}
          <VariantsEditor form={form} setForm={setForm} />

          {/* ─── CJ Dropshipping linkage ─── */}
          <div className="mt-6 border-t border-white/10 pt-6" data-testid="cj-linkage-section">
            <div className="text-xs font-bold uppercase tracking-[0.25em] text-[#C5CDD8]">
              CJ Dropshipping (auto-fulfillment)
            </div>
            <p className="mt-2 text-[10px] text-white/45 leading-relaxed">
              Renseignez le <strong>CJ Variant ID</strong> (vid) du produit dans votre compte
              cjdropshipping.com pour activer la livraison automatique. Sans cela, la
              commande sera créée chez Stripe mais ne sera <em>pas</em> poussée à CJ.
            </p>
            <div className="mt-4 grid sm:grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] font-bold uppercase tracking-widest text-white/55 mb-1 block">
                  CJ Variant ID (vid)
                </label>
                <input
                  type="text"
                  placeholder="ex: 12345-AB-XL"
                  value={form.cj_vid || ""}
                  onChange={(e) => setForm({ ...form, cj_vid: e.target.value })}
                  className="w-full bg-[#001F3F] border border-white/20 text-white text-sm px-3 py-2 focus:outline-none focus:border-white"
                  data-testid="form-product-cj-vid"
                />
              </div>
              <div>
                <label className="text-[10px] font-bold uppercase tracking-widest text-white/55 mb-1 block">
                  CJ Product ID (pid) — optionnel
                </label>
                <input
                  type="text"
                  placeholder="Sync de stock global"
                  value={form.cj_pid || ""}
                  onChange={(e) => setForm({ ...form, cj_pid: e.target.value })}
                  className="w-full bg-[#001F3F] border border-white/20 text-white text-sm px-3 py-2 focus:outline-none focus:border-white"
                  data-testid="form-product-cj-pid"
                />
              </div>
            </div>
          </div>

          <DialogFooter>
            <button
              onClick={close}
              className="border border-white/20 bg-transparent text-white hover:border-white uppercase tracking-widest text-xs font-bold px-6 py-3"
            >
              Annuler
            </button>
            <button
              onClick={save}
              className="border border-white bg-white text-[#001F3F] hover:bg-transparent hover:text-white transition uppercase tracking-widest text-xs font-bold px-6 py-3"
              data-testid="form-product-save"
            >
              Enregistrer
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Kpi({ icon: Icon, label, value, hint, testid }) {
  return (
    <div
      className="border border-white/10 bg-[#0A2A4F] p-5"
      data-testid={testid}
    >
      <div className="flex items-center justify-between text-[#C5CDD8] mb-3">
        <span className="text-[10px] font-bold uppercase tracking-[0.25em]">
          {label}
        </span>
        {Icon && <Icon size={16} strokeWidth={1.25} />}
      </div>
      <div className="font-display text-3xl font-bold text-white leading-none">
        {value}
      </div>
      {hint && (
        <div className="text-xs text-white/50 mt-2 uppercase tracking-widest">
          {hint}
        </div>
      )}
    </div>
  );
}

function VariantsEditor({ form, setForm }) {
  const attributes = Array.isArray(form.variant_attributes) ? form.variant_attributes : [];
  const variants = Array.isArray(form.variants) ? form.variants : [];

  const setAttrs = (next) => setForm({ ...form, variant_attributes: next });
  const setVariants = (next) => setForm({ ...form, variants: next });

  const addAttribute = () => {
    const name = window.prompt("Nom de l'attribut (ex: Couleur, Taille)")?.trim();
    if (!name || attributes.includes(name)) return;
    setAttrs([...attributes, name]);
  };
  const removeAttribute = (attr) => {
    setAttrs(attributes.filter((a) => a !== attr));
    setVariants(
      variants.map((v) => {
        const rest = { ...(v.attributes || {}) };
        delete rest[attr];
        return { ...v, attributes: rest };
      })
    );
  };

  const addVariant = () => {
    if (attributes.length === 0) {
      toast.error("Ajoutez au moins un attribut avant de créer une variante");
      return;
    }
    const id = "v-" + Math.random().toString(36).slice(2, 9);
    const attrs = {};
    attributes.forEach((a) => (attrs[a] = ""));
    setVariants([
      ...variants,
      { id, label: "", attributes: attrs, sku: null, price_delta: 0, stock: 0, image: "" },
    ]);
  };
  const removeVariant = (idx) => {
    setVariants(variants.filter((_, i) => i !== idx));
  };
  const updateVariant = (idx, patch) => {
    setVariants(variants.map((v, i) => (i === idx ? { ...v, ...patch } : v)));
  };
  const updateVariantAttr = (idx, attr, value) => {
    setVariants(
      variants.map((v, i) =>
        i === idx ? { ...v, attributes: { ...(v.attributes || {}), [attr]: value } } : v
      )
    );
  };

  const computeLabel = (v) =>
    attributes.map((a) => v.attributes?.[a]).filter(Boolean).join(" · ");

  return (
    <div className="mt-6 border-t border-white/10 pt-6" data-testid="variants-editor">
      <div className="flex items-center justify-between">
        <div className="text-xs font-bold uppercase tracking-[0.25em] text-[#C5CDD8]">
          Variantes (couleur, taille…)
        </div>
        <button
          type="button"
          onClick={addAttribute}
          className="text-[10px] font-bold uppercase tracking-widest text-white border border-white/30 hover:bg-white/10 px-3 py-2"
          data-testid="variants-add-attribute"
        >
          + Attribut
        </button>
      </div>

      {attributes.length === 0 ? (
        <p className="mt-3 text-xs text-white/50">
          Aucun attribut défini. Cliquez sur "+ Attribut" pour démarrer (ex : Couleur).
        </p>
      ) : (
        <>
          <div className="mt-3 flex flex-wrap gap-2">
            {attributes.map((a) => (
              <span
                key={a}
                className="inline-flex items-center gap-2 bg-white/5 border border-white/15 px-2.5 py-1 text-xs text-white"
              >
                {a}
                <button
                  type="button"
                  onClick={() => removeAttribute(a)}
                  className="text-white/50 hover:text-[#FF6B6B]"
                  aria-label={`Retirer ${a}`}
                >
                  ×
                </button>
              </span>
            ))}
          </div>

          <div className="mt-4 space-y-3">
            {variants.map((v, idx) => (
              <div
                key={v.id || idx}
                className="border border-white/10 bg-[#0A2A4F]/60 p-3"
                data-testid={`variant-row-${idx}`}
              >
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {attributes.map((a) => (
                    <input
                      key={a}
                      placeholder={a}
                      value={v.attributes?.[a] || ""}
                      onChange={(e) => updateVariantAttr(idx, a, e.target.value)}
                      onBlur={() =>
                        !v.label && updateVariant(idx, { label: computeLabel(v) })
                      }
                      className="bg-[#001F3F] border border-white/20 text-white text-xs px-2 py-1.5"
                    />
                  ))}
                  <input
                    type="number"
                    step="0.01"
                    placeholder="Δ Prix (€)"
                    value={v.price_delta ?? 0}
                    onChange={(e) =>
                      updateVariant(idx, { price_delta: parseFloat(e.target.value) || 0 })
                    }
                    className="bg-[#001F3F] border border-white/20 text-white text-xs px-2 py-1.5"
                  />
                  <input
                    type="number"
                    placeholder="Stock"
                    value={v.stock ?? 0}
                    onChange={(e) =>
                      updateVariant(idx, { stock: parseInt(e.target.value) || 0 })
                    }
                    className="bg-[#001F3F] border border-white/20 text-white text-xs px-2 py-1.5"
                  />
                </div>
                <div className="mt-2 grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-2 items-center">
                  <input
                    placeholder="Image URL (optionnel, sinon = image principale)"
                    value={v.image || ""}
                    onChange={(e) => updateVariant(idx, { image: e.target.value })}
                    className="bg-[#001F3F] border border-white/20 text-white text-xs px-2 py-1.5"
                  />
                  <button
                    type="button"
                    onClick={() => removeVariant(idx)}
                    className="text-[10px] font-bold uppercase tracking-widest text-[#FF6B6B] border border-[#FF6B6B]/40 hover:bg-[#FF6B6B]/10 px-3 py-1.5"
                  >
                    Retirer
                  </button>
                </div>
              </div>
            ))}
            <button
              type="button"
              onClick={addVariant}
              className="w-full border border-dashed border-white/30 text-white/70 hover:text-white hover:border-white/60 text-xs uppercase tracking-widest font-bold py-2.5"
              data-testid="variants-add-row"
            >
              + Ajouter une variante
            </button>
          </div>
        </>
      )}
    </div>
  );
}

